import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { COMPANY_INFO, SOLCELLEVASK_INFO } from "../data/siteData";
import SolcellevaskCalculator from "../components/SolcellevaskCalculator";
import ReviewSlider from "../components/ReviewSlider";
import CmsGallery from "../components/CmsGallery";

export default function SolcellevaskPage() {
  const [, setCalculationData] = useState(null);

  const benefits = [
    {
      icon: "fa-location-dot",
      title: "Lokalt på Vestsjælland",
      desc: "Vi kender området og er hurtigt fremme"
    },
    {
      icon: "fa-bolt",
      title: "Hurtig tid",
      desc: "Fleksible aftaler der passer til dig"
    },
    {
      icon: "fa-tag",
      title: "Skarpe priser",
      desc: "Fast pris på 22 kr. pr. panel"
    },
    {
      icon: "fa-circle-check",
      title: "Professionelt resultat",
      desc: "100% rent afmineraliseret vand"
    }
  ];

  const packages = [
    {
      id: "halvaar",
      name: "Halvårsabonnement",
      frequency: "2 gange årligt",
      badge: "Mest populære",
      description: "Vask forår og efterår for maksimal solproduktion og optimal virkningsgrad hele året.",
      features: [
        "2 faste årlige vaske (forår & efterår)",
        "100% afmineraliseret osmosevand",
        "Fjerner pollen, sod, mos og fugleklatter",
        "Skånsom vask med specialbørster fra jorden",
        "Ingen binding eller skjulte gebyrer"
      ]
    },
    {
      id: "aar",
      name: "Årsabonnement",
      frequency: "1 gang årligt",
      badge: "Forårsklargøring",
      description: "Fast forårsklargøring, så dine paneler yder optimalt i den solrige højsæson.",
      features: [
        "1 fast årlig forårsvask",
        "100% afmineraliseret osmosevand",
        "Genskaber op til 10–25% tabt produktion",
        "Skånsom vask uden unødig færdsel på taget",
        "Ingen binding eller opsigelsesvarsel"
      ]
    },
    {
      id: "enkelt",
      name: "Enkeltstående vask",
      frequency: "Efter behov",
      badge: "Fleksibel",
      description: "Til dig der ønsker en grundig og professionel engangsvask uden fast aftale.",
      features: [
        "Engangsbesøg når du har behov",
        "100% afmineraliseret osmosevand",
        "Stribefrit resultat uden kalkrester",
        "Ingen binding eller forpligtelser",
        "Hurtig og fleksibel udkørsel"
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Solcellevask Slagelse &amp; Vestsjælland | Kun 22 kr. pr. panel</title>
        <meta
          name="description"
          content="Få professionel solcellevask i Slagelse og på Vestsjælland for kun 22 kr. pr. panel. Skånsom vask med 100% afmineraliseret osmosevand samt fleksible halvårs- og årsabonnementer."
        />
        <meta
          property="og:title"
          content="Solcellevask Slagelse &amp; Vestsjælland | Kun 22 kr. pr. panel"
        />
        <meta
          property="og:description"
          content="Få professionel solcellevask i Slagelse og på Vestsjælland for kun 22 kr. pr. panel. Skånsom vask med 100% afmineraliseret osmosevand samt fleksible halvårs- og årsabonnementer."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/hero-solcellevask-nf-polering.png"
        />
        <link rel="canonical" href="https://nf-polering.dk/solcellevask" />
      </Helmet>

      {/* Hero */}
      <section className="relative bg-slate-50 text-slate-900 pt-12 pb-20 lg:pt-16 lg:pb-24 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-tight">
                Solcellevask fra kun 22 kr. pr. panel
              </h1>

              <p className="text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed">
                Få hurtig tid, skarpe priser og et resultat der holder. Ring nu eller få dit uforpligtende tilbud med det samme.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white font-bold text-base transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 transform active:scale-95 whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring og få en pris</span>
                </a>
                <a
                  href="#beregner"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white hover:bg-slate-100 text-slate-900 font-semibold text-base border border-slate-300 transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 whitespace-nowrap shadow-sm"
                >
                  <i className="fa-solid fa-calculator text-slate-800" aria-hidden="true" />
                  <span>Beregn din pris</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-none overflow-hidden shadow-xl border border-slate-200 bg-white">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/hero-solcellevask-nf-polering.png"
                  alt="Professionel solcellevask"
                  className="w-full h-80 sm:h-96 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 sm:py-14 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 text-center">
            {benefits.map((benefit, i) => (
              <div
                key={i}
                className="flex flex-col items-center p-2"
              >
                <div className="w-12 h-12 rounded-full bg-cyan-50 border border-cyan-100 flex items-center justify-center text-cyan-600 mb-3 text-lg">
                  <i className={`fa-solid ${benefit.icon}`} aria-hidden="true" />
                </div>
                <h3 className="font-bold text-slate-900 text-base leading-tight">
                  {benefit.title}
                </h3>
                <p className="mt-1.5 text-slate-600 text-sm leading-relaxed w-full mx-auto">
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solcellevask Packages & Pricing Table */}
      <section className="py-16 sm:py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-100 text-cyan-900 font-bold text-xs uppercase tracking-wider mb-4">
              <i className="fa-solid fa-tag text-cyan-600" aria-hidden="true" />
              <span>Fast pris: 22 kr. pr. panel uanset løsning</span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Solcellevask Pakker &amp; Abonnementer
            </h2>
            <p className="mt-3 text-slate-600 text-base">
              Vælg den aftaleform der passer bedst til dit behov – prisen er altid 22 kr. pr. panel
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className="relative rounded-none p-7 sm:p-8 flex flex-col justify-between transition-all bg-white border border-slate-200 shadow-sm"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-cyan-700 bg-cyan-50 px-2.5 py-1 rounded-full border border-cyan-200/60">
                      {pkg.badge}
                    </span>
                    <span className="text-xs font-semibold text-slate-500">
                      {pkg.frequency}
                    </span>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-3">
                    {pkg.name}
                  </h3>

                  <p className="text-xs sm:text-sm text-slate-600 mb-6 leading-relaxed">
                    {pkg.description}
                  </p>

                  <div className="h-px bg-slate-100 mb-6" />

                  <ul className="space-y-3 mb-8 text-sm text-slate-700">
                    {pkg.features.map((feat, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2.5">
                        <i className="fa-solid fa-check text-cyan-600 text-sm mt-0.5 shrink-0" aria-hidden="true" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <a
                    href={COMPANY_INFO.phoneTel}
                    className="w-full py-3.5 px-4 rounded-full font-bold text-sm text-center transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 transform active:scale-95 flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    <span>VÆLG LØSNING</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-500 mt-8 max-w-2xl mx-auto font-medium">
            Alle priser er inkl. moms. Ingen bindingsperiode på abonnementer.
          </p>
        </div>
      </section>

      {/* Interactive Price Calculator Section */}
      <section id="beregner" className="py-16 sm:py-24 bg-slate-50 border-t border-slate-200 scroll-mt-24 sm:scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SolcellevaskCalculator onSelectPlan={(data) => setCalculationData(data)} />
        </div>
      </section>

      {/* Verbatim Content Section - Same layout as FliserensPage & CityPage */}
      <section className="py-16 sm:py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24 sm:space-y-32">
          {/* Row 1: Image Left, Text Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 lg:order-2 space-y-3.5 text-slate-700 leading-relaxed text-base">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                Professionel solcellevask i Slagelse, Korsør, Sorø og hele Vestsjælland
              </h2>
              <p>
                Er dine solceller dækket af støv, pollen, fugleklatter eller mos og belægninger? Hos <strong>NF-Polering</strong> hjælper vi med at holde dine solceller helt rene, så de producerer maksimal grøn strøm året rundt.
              </p>
              <p className="font-semibold text-slate-900">
                Lige nu tilbyder vi professionel solcellevask fra kun 22 kr. pr. panel.
              </p>
              <p>
                Det betyder, at du får en skånsom og effektiv vask uden skadelige kemikalier. Vi anvender 100% rent afmineraliseret osmosevand, som fordamper stribefrit uden at efterlade kalkrester på overfladen.
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-slate-900 font-semibold text-sm">
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Skånsom solcellevask</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>100% osmosevand</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Halvårs- og årsabonnement</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Kun 22 kr. pr. panel</span>
                </li>
              </ul>

              <p className="pt-2 font-medium text-slate-900">
                Få et gratis og uforpligtende tilbud på dine solceller i dag.
              </p>

              <div className="pt-3">
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 transform active:scale-95"
                >
                  <span>Få et tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 lg:order-1">
              <div className="rounded-none overflow-hidden border border-slate-200 shadow-sm">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/hero-solcellevask-nf-polering.png"
                  alt="Professionel vask af solceller på tag"
                  className="w-full h-80 sm:h-96 object-cover"
                />
              </div>
            </div>
          </div>

          {/* Row 2: Text Left, Image Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 lg:order-1 space-y-3.5 text-slate-700 leading-relaxed text-base">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                Giv dine solceller maksimalt energiudbytte
              </h2>
              <p>
                Almindeligt regnvand kan sjældent fjerne fastgroet snavs, sod, pollen og fugleklatter. Selv et tyndt lag snavs reflekterer solens stråler og kan reducere dit energitilskud med helt op til 10–25%.
              </p>
              <p>
                Med en professionel <strong>solcellevask</strong> fra NF-Polering sikrer du, at panelerne yder optimalt. Vi vasker skånsomt med bløde specialbørster på lette kulfiberteleskopstænger uden unødig færdsel på dit tag.
              </p>
              <p>
                Hos NF-Polering tilbyder vi solcellevask til både private boliger og erhverv og vasker blandt andet:
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-slate-900 font-semibold text-sm">
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Private villaer og parcelhuse</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Erhvervsbygninger og haller</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Tagmonterede solcelleanlæg</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Jordmonterede anlæg</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Halvårs- og helårsabonnementer</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Fast pris på 22 kr. pr. panel</span>
                </li>
              </ul>

              <p className="pt-2">
                Vælg mellem vores populære <strong>halvårsabonnement</strong> (forår og efterår) eller <strong>årsabonnement</strong> (forår) til den samme faste lave pris på <strong>22 kr. pr. panel</strong>.
              </p>

              <div className="pt-3">
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 transform active:scale-95"
                >
                  <span>Få et tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 lg:order-2">
              <div className="rounded-none overflow-hidden border border-slate-200 shadow-sm">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/solcellevask-rene-paneler.png"
                  alt="Rene og skinnende solcellepaneler efter professionel vask"
                  className="w-full h-80 sm:h-96 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Gallery */}
      <CmsGallery
        slug="solcellevask"
        title="Billeder af solcellevask"
        subtitle="Se vores arbejde og rene solcelleanlæg på Vestsjælland."
      />

      {/* Reviews */}
      <ReviewSlider />
    </>
  );
}
